import React from 'react';
import type { EnquiryFormData, SelectedVase } from '../../../types';
import { useProductRecords } from '../../../hooks/useProductRecords';
import { REQUEST_FLOW_FIELD_IDS } from '../../../lib/flowFieldExtensions';
import { requestItemsToSelectedVases } from '../../../lib/flowNodeRuntime';
import { VASE_FLOW_FIELD_IDS } from '../../../lib/flows';
import { resolveSectionOrPageFlow } from '../../../lib/flowBlockResolution';
import { isCompositionSection } from '../../../lib/sectionLayoutMode';
import {
  DEFAULT_FONT_LIBRARY,
  type DesignSystemTypography,
} from '../../../lib/typography';
import type { RichTextFontOption } from '../../admin/pageLayout/fields';
import { useFlowContext } from '../../flow/FlowProvider';
import { useImageTransformInteractions } from '../imageEditing/useImageTransformInteractions';
import { isBuilderAnchorBlock } from '../layoutEngine/blockPolicies/resolveBlockPolicy';
import {
  isBuilderLayerHidden,
  isBuilderLayerLocked,
  resolveBuilderBlockStyleMeta,
  resolveBuilderBlockLabel,
  resolveResponsiveBlockLayout,
  resolveResponsiveBlockTransform,
  resolveRuntimeViewport,
  TOTAL_COLUMNS,
} from '../layoutEngine/blockFrame';
import { shouldSuppressBuilderHoverChrome } from '../layoutEngine/blockHoverChrome';
import { resolveCanvasMinRows, resolveSectionRows, SECTION_ROW_HEIGHT_PX } from '../layoutEngine/sectionSizing';
import {
  isSectionBackdropModelViewerBlock,
  resolveSectionSceneLayerMode,
} from '../blockRenderers/modelViewerSceneBackdrop';
import SelectionOverlay from '../layoutEngine/SelectionOverlay';
import {
  editorBoundsCanvasOverlayStyle,
  editorBoundsFrameOverlayStyle,
  resolveEditorBoundsFromDom,
  type EditorBoundsBox,
  useEditorBoundsRegistry,
} from '../layoutEngine/editorBounds';
import { useBlockTransformEngine } from '../layoutEngine/useBlockTransformEngine';
import { buildHoverGeometryDebug } from '../layoutEngine/geometryAudit';
import {
  emitMovementDebug,
  isMovementDebugRequested,
} from '../layoutEngine/movementDebug';
import type {
  CanvasBlockLayoutChangeMeta,
  CanvasSectionBlock,
  CanvasSectionBlockLayout,
  HomepageDesignSystemSettings,
  HomepageSectionSettings,
} from '../sectionRegistry';
import { SectionElementLayerCanvas } from './SectionElementLayerCanvas';
import { useLayerSize } from '../hooks/useLayerSize';
import { useModelViewerRandomSelections } from '../hooks/useModelViewerRandomSelections';
import { useProductCardScaleControls } from '../hooks/useProductCardScaleControls';
import { useSectionElementRuntimeDiagnostics } from '../hooks/useSectionElementRuntimeDiagnostics';

function resolveSelectionOverlayLabel(block: CanvasSectionBlock): string {
  const label = resolveBuilderBlockLabel(block);
  if (block.type !== 'model-viewer') return label;
  const content =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : {};
  const usesSectionScene =
    resolveSectionSceneLayerMode(content, {
      immersiveSurface: true,
      publicLightingMode: 'hero',
      stageAtmosphere: String(content.stageAtmosphere ?? '').toLowerCase(),
    }) === 'section-backdrop';
  if (!usesSectionScene) return label;
  return `${label} — use Inspector for vase position (random models share this framing)`;
}

interface SectionElementLayerProps {
  sectionId?: string;
  settings?: HomepageSectionSettings;
  designSystemOverride?: HomepageDesignSystemSettings;
  enquiryEmailTo?: string;
  onSelectVase?: (vase: SelectedVase) => void;
  onEnquirySubmit?: (formData: EnquiryFormData) => void;
  onNavigateToPage?: (href: string, meta?: { pageKey?: string }) => void;
  className?: string;
  adminPreview?: {
      enabled: boolean;
    viewport?: 'desktop' | 'tablet' | 'mobile';
      interactionMode?: 'none' | 'layout' | 'scale' | 'image';
    isImageAdjustMode?: boolean;
    activeAdjustBlockId?: string | null;
    onExitImageAdjust?: () => void;
    previewMode?: 'edit' | 'interact' | 'immersive';
    showPlacementGrid?: boolean;
    showGapGuides?: boolean;
    showLayoutFrames?: boolean;
    selectedBlockId?: string | null;
    selectedBlockIds?: string[];
    editingTextBlockId?: string | null;
    styleOverrides?: Record<string, Record<string, unknown> | undefined>;
    layoutOverrides?: Record<string, CanvasSectionBlockLayout | undefined>;
    showSelectionChrome?: boolean;
    onSelectBlock?: (blockId: string | null) => void;
    onStartTextEdit?: (blockId: string) => void;
    onUpdateTextContent?: (blockId: string, lexicalState: string) => void;
    onEndTextEdit?: (blockId: string) => void;
    onOpenBlockContent?: (blockId: string, options?: { galleryItemId?: string }) => void;
    onBlockLayoutChange?: (
      blockId: string,
      layout: CanvasSectionBlockLayout,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onPreviewBlockLayout?: (
      blockId: string,
      layout: CanvasSectionBlockLayout | null,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onCommitBlockLayout?: (
      blockId: string,
      layout: CanvasSectionBlockLayout,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onBlockScaleChange?: (blockId: string, scale: { x?: number; y?: number }) => void;
    onDeleteBlock?: (blockId: string) => void;
    onPreviewBlockStyle?: (blockId: string, styleOverride: Record<string, unknown> | null) => void;
    onCommitBlockStyle?: (
      blockId: string,
      styleOverride: Record<string, unknown> | null,
      commitViewport?: 'desktop' | 'tablet' | 'mobile'
    ) => void;
    onTextAutoHeightMeasured?: (blockId: string, rows: number) => void;
    onBlockAutoHeightMeasured?: (blockId: string, rows: number) => void;
    onProductCardDebugSnapshot?: (blockId: string | null, snapshot: Record<string, unknown> | null) => void;
    onConnectedBlockDebugSnapshot?: (snapshot: Record<string, unknown> | null) => void;
    onTransformingChange?: (isTransforming: boolean) => void;
  };
}

const SNAP_THRESHOLD_PX = 8;
const DIAGNOSTIC_FORCE_TEXT_FRAME_TRUTH = false;

function editorBoundsNeedsContentRefresh(bounds: EditorBoundsBox | undefined) {
  return Boolean(
    bounds &&
      (bounds.mode === 'content' || bounds.mode === 'text-paragraph') &&
      !bounds.contentBox
  );
}

function getCanvasBlocks(settings?: HomepageSectionSettings) {
  const blocks = Array.isArray(settings?.canvas?.blocks) ? settings!.canvas!.blocks : [];
  const minRows = resolveCanvasMinRows(
    blocks,
    settings?.canvas?.minRows,
    {
      floorRows: 6,
      fallbackRows: 12,
    },
    settings?.canvas?.minRowsMode
  );
  return { blocks, minRows };
}

const SectionElementLayer: React.FC<SectionElementLayerProps> = ({
  sectionId,
  settings,
  designSystemOverride,
  enquiryEmailTo,
  onSelectVase,
  onEnquirySubmit,
  onNavigateToPage,
  adminPreview,
  className,
}) => {
  const flow = useFlowContext();
  const isAdmin = adminPreview?.enabled === true;
  const { products: productRecords } = useProductRecords({ admin: isAdmin });
  const { blocks, minRows } = React.useMemo(() => getCanvasBlocks(settings), [settings]);
  const { visibleColourCombinations, viewerRandomSelections } = useModelViewerRandomSelections(blocks);
  const typography = designSystemOverride as Partial<DesignSystemTypography> | undefined;
  const fontLibrary = { ...DEFAULT_FONT_LIBRARY, ...(typography?.fontLibrary ?? {}) };
  const isCompositionCanvas = isCompositionSection({
    type: 'canvas',
    settings,
  });
  const isImmersiveCanvas = settings?.canvas?.presentation === 'immersive';
  const usesFixedFrameText = isCompositionCanvas || isImmersiveCanvas;
  const useFluidSectionTypography = !isCompositionCanvas;
  const richTextFontOptions = React.useMemo<RichTextFontOption[]>(
    () =>
      Object.entries(fontLibrary).map(([key, value]) => ({
        value: key,
        label: key,
        previewFamily: value,
        styleValue: value,
      })),
    [fontLibrary]
  );
  const renderBlocks = React.useMemo(
    () =>
      (isAdmin ? blocks : blocks.filter((block) => !isBuilderAnchorBlock(block))).map((block) => {
        const activeLayout = resolveResponsiveBlockLayout(
          block,
          isAdmin ? adminPreview?.viewport : undefined
        );
        const previewLayoutOverride = isAdmin ? adminPreview?.layoutOverrides?.[block.id] : undefined;
        const previewStyleOverride = adminPreview?.styleOverrides?.[block.id];
        const resolvedLayout = previewLayoutOverride ?? activeLayout;
        if (!previewStyleOverride || !isAdmin) {
          return resolvedLayout === block.layout ? block : { ...block, layout: resolvedLayout };
        }
        const baseStyle =
          block.style && typeof block.style === 'object'
            ? (block.style as Record<string, unknown>)
            : {};
        return {
          ...block,
          layout: resolvedLayout,
          style: {
            ...baseStyle,
            ...previewStyleOverride,
          },
        };
      }),
    [adminPreview?.layoutOverrides, adminPreview?.styleOverrides, adminPreview?.viewport, blocks, isAdmin]
  );
  const selectedProductIds = React.useMemo(() => {
    const activeFlow = resolveSectionOrPageFlow(flow.flows, sectionId ?? null);
    if (!activeFlow) return new Set<number>();

    const requestSelectedVases = requestItemsToSelectedVases(
      flow.getFlowValue(activeFlow.id, REQUEST_FLOW_FIELD_IDS.requestItems)
    );
    if (requestSelectedVases.length > 0) {
      return new Set(
        requestSelectedVases
          .map((product) => product.id)
          .filter((productId) => Number.isFinite(productId))
      );
    }

    const selectedProduct = flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenProduct);
    return selectedProduct &&
      typeof selectedProduct === 'object' &&
      Number.isFinite((selectedProduct as { id?: unknown }).id)
      ? new Set([Number((selectedProduct as { id: number }).id)])
      : new Set<number>();
  }, [flow]);
  const moveMode = isAdmin && adminPreview?.interactionMode === 'layout';
  const scaleMode = isAdmin && adminPreview?.interactionMode === 'scale';
  const isImageAdjustMode = isAdmin && adminPreview?.isImageAdjustMode === true;
  const activeAdjustBlockId = adminPreview?.activeAdjustBlockId ?? null;
  const isInteractPreview = adminPreview?.previewMode === 'interact';
  const isImmersivePreview = adminPreview?.previewMode === 'immersive';
  const selectedBlockId = adminPreview?.selectedBlockId ?? null;
  const editingTextBlockId = adminPreview?.editingTextBlockId ?? null;
  const selectedBlockIds = React.useMemo(
    () => new Set((adminPreview?.selectedBlockIds ?? []).filter(Boolean)),
    [adminPreview?.selectedBlockIds]
  );
  const showSelectionChrome = adminPreview?.showSelectionChrome !== false;
  const [frozenProjectionRows, setFrozenProjectionRows] = React.useState<number | null>(null);
  const [hoveredProductCardId, setHoveredProductCardId] = React.useState<string | null>(null);
  const [hoveredBlockId, setHoveredBlockId] = React.useState<string | null>(null);
  const [activeModelPreview, setActiveModelPreview] = React.useState<{
    modelPath: string;
    vaseId?: number;
    title?: string;
  } | null>(null);

  const projectionRows = React.useMemo(
    () =>
      resolveSectionRows(settings, {
        floorRows: minRows,
        fallbackRows: minRows,
      }),
    [minRows, settings]
  );

  const activeProjectionRows = frozenProjectionRows ?? projectionRows;
  const { layerRef, layerSize } = useLayerSize(activeProjectionRows);
  const columnWidthPx = layerSize.width > 0 ? layerSize.width / TOTAL_COLUMNS : 0;
  const rowHeightPx = layerSize.height > 0 ? layerSize.height / Math.max(1, activeProjectionRows) : SECTION_ROW_HEIGHT_PX;
  const resolveEditorBoundsForLayer = React.useCallback(
    (block: CanvasSectionBlock): EditorBoundsBox | null => {
      const layer = layerRef.current;
      if (!layer || columnWidthPx <= 0 || rowHeightPx <= 0) return null;
      return resolveEditorBoundsFromDom(block, layer, {
        colWidth: columnWidthPx,
        rowHeight: rowHeightPx,
      });
    },
    [columnWidthPx, layerRef, rowHeightPx]
  );
  const editorBoundsByBlockId = useEditorBoundsRegistry({
    enabled: isAdmin,
    layerRef,
    blocks: renderBlocks,
    colWidth: columnWidthPx,
    rowHeight: rowHeightPx,
  });

  const { alignmentGuides, isTransforming, isPlacementDrag, beginTransform, beginPlacementDrag, moveTransform, endTransform, selectBlock } =
    useBlockTransformEngine({
      enabled: moveMode,
      isAdmin,
      layerRef,
    blocks: renderBlocks,
      selectedBlockIds: Array.from(selectedBlockIds),
      editorBoundsByBlockId,
      viewport: resolveRuntimeViewport(isAdmin ? adminPreview?.viewport : undefined),
      maxRows: activeProjectionRows,
      allowRowExpansion: settings?.canvas?.presentation !== 'immersive',
      totalColumns: TOTAL_COLUMNS,
      snapThresholdPx: SNAP_THRESHOLD_PX,
      rowHeightPx,
      onSelectBlock: adminPreview?.onSelectBlock,
      onBlockLayoutChange: adminPreview?.onBlockLayoutChange,
      onPreviewBlockLayout: adminPreview?.onPreviewBlockLayout,
      onCommitBlockLayout: adminPreview?.onCommitBlockLayout,
      onPreviewBlockStyle: adminPreview?.onPreviewBlockStyle,
      onCommitBlockStyle: adminPreview?.onCommitBlockStyle,
      onDuplicateCanvasBlockForDrag: adminPreview?.onDuplicateCanvasBlockForDrag,
      onPlacementCancel: adminPreview?.onPlacementCancel,
      onPlacementCommit: adminPreview?.onPlacementCommit,
    });

  const {
    activeInteraction: activeImageInteraction,
    beginSurfaceMove,
    moveSurfaceMove,
    endSurfaceMove,
    handleWheelZoom,
    beginHandleInteraction,
    moveHandleInteraction,
    endHandleInteraction,
  } = useImageTransformInteractions({
    isImageAdjustMode,
    activeAdjustBlockId,
    selectedBlockId,
    onPreviewBlockStyle: adminPreview?.onPreviewBlockStyle,
    onCommitBlockStyle: adminPreview?.onCommitBlockStyle,
  });

  const {
    productCardWrapperNodesRef,
    productCardArticleNodesRef,
    productCardOverlayNodesRef,
    productCardRuntimeSnapshotsRef,
    setConnectedBlockDebugSnapshot,
    setTextMeasureNode,
    setProductCardMeasureNode,
  } = useSectionElementRuntimeDiagnostics({
    activeProjectionRows,
    adminPreview,
    isAdmin,
    isTransforming,
    layerRef,
    layerSize,
    moveMode,
    renderBlocks,
    scaleMode,
    sectionId,
    selectedBlockId,
    usesFixedFrameText,
  });

  const { beginScale, moveScale, endScale } = useProductCardScaleControls({
    scaleMode,
    onBlockScaleChange: adminPreview?.onBlockScaleChange,
    selectBlock,
  });

  React.useEffect(() => {
    if (isTransforming) {
      setFrozenProjectionRows((current) => current ?? projectionRows);
      setHoveredBlockId(null);
      return;
    }
    setFrozenProjectionRows(null);
  }, [isTransforming, projectionRows]);

  const lastLayerPointerRef = React.useRef<{ x: number; y: number } | null>(null);
  const processedPlacementRequestIdRef = React.useRef(0);

  React.useEffect(() => {
    if (!isAdmin) return;
    const handlePointerMove = (event: PointerEvent) => {
      lastLayerPointerRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, [isAdmin]);

  React.useEffect(() => {
    const request = adminPreview?.placementDragRequest;
    if (!request || !moveMode || !beginPlacementDrag) return;
    if (request.requestId === processedPlacementRequestIdRef.current) return;

    const allReady = request.blockIds.every((blockId) =>
      renderBlocks.some((block) => block.id === blockId)
    );
    if (!allReady) return;

    const primaryBlock = renderBlocks.find((block) => block.id === request.primaryBlockId);
    if (!primaryBlock) return;

    processedPlacementRequestIdRef.current = request.requestId;

    window.requestAnimationFrame(() => {
      let clientX = request.clientX;
      let clientY = request.clientY;
      if (clientX == null || clientY == null) {
        const layer = layerRef.current;
        const blockRoot = layer?.querySelector(
          `[data-builder-block-id="${request.primaryBlockId}"]`
        ) as HTMLElement | null;
        const blockRect = blockRoot?.getBoundingClientRect();
        if (blockRect) {
          clientX = blockRect.left + blockRect.width / 2;
          clientY = blockRect.top + blockRect.height / 2;
        } else {
          clientX = lastLayerPointerRef.current?.x ?? window.innerWidth / 2;
          clientY = lastLayerPointerRef.current?.y ?? window.innerHeight / 2;
        }
      }
      beginPlacementDrag(primaryBlock, clientX, clientY, {
        groupBlockIds: request.blockIds,
        placementBlockIds: request.blockIds,
        placementReferenceBlockId: request.referenceBlockId,
      });
      adminPreview?.clearPlacementDragRequest?.();
    });
  }, [
    adminPreview?.clearPlacementDragRequest,
    adminPreview?.placementDragRequest,
    beginPlacementDrag,
    layerRef,
    moveMode,
    renderBlocks,
  ]);

  React.useEffect(() => {
    adminPreview?.onTransformingChange?.(isTransforming);
  }, [adminPreview?.onTransformingChange, isTransforming]);

  React.useEffect(() => {
    if (!hoveredBlockId || !isMovementDebugRequested()) return;
    const block = renderBlocks.find((candidate) => candidate.id === hoveredBlockId);
    if (!block) return;
    const bounds =
      resolveEditorBoundsForLayer(block) ?? editorBoundsByBlockId[hoveredBlockId];
    const isSelected =
      selectedBlockId === hoveredBlockId || selectedBlockIds.has(hoveredBlockId);
    emitMovementDebug({
      source: 'hover',
      phase: 'visual',
      blockId: hoveredBlockId,
      hoverGeometry: buildHoverGeometryDebug({ block, bounds, isSelected }),
    });
  }, [
    editorBoundsByBlockId,
    hoveredBlockId,
    renderBlocks,
    resolveEditorBoundsForLayer,
    selectedBlockId,
    selectedBlockIds,
  ]);

  const resolveCanvasFrameOverlayStyle = React.useCallback(
    (block: CanvasSectionBlock): React.CSSProperties | undefined => {
      if (columnWidthPx <= 0 || rowHeightPx <= 0) return undefined;
      const blockStyleMeta = resolveBuilderBlockStyleMeta(block);
      const activeViewport = resolveRuntimeViewport(isAdmin ? adminPreview?.viewport : undefined);
      const runtimeLayout = resolveResponsiveBlockLayout(
        block,
        isAdmin ? adminPreview?.viewport : undefined
      );
      const isolateAdminViewport = isAdmin && Boolean(adminPreview?.viewport);
      const blockTransform = resolveResponsiveBlockTransform(blockStyleMeta, activeViewport, {
        isolateAdminViewport,
      });
      const width = Math.max(1, runtimeLayout.w * columnWidthPx * blockTransform.widthScale);
      const height = Math.max(1, runtimeLayout.h * rowHeightPx * blockTransform.heightScale);
      const offsetXPx =
        typeof blockTransform.offsetXPx === 'number'
          ? blockTransform.offsetXPx
          : (blockTransform.offsetX / 100) * width;
      const offsetYPx =
        typeof blockTransform.offsetYPx === 'number'
          ? blockTransform.offsetYPx
          : (blockTransform.offsetY / 100) * height;
      return {
        left: (runtimeLayout.x - 1) * columnWidthPx + offsetXPx,
        top: (runtimeLayout.y - 1) * rowHeightPx + offsetYPx,
        width,
        height,
      };
    },
    [adminPreview?.viewport, columnWidthPx, isAdmin, rowHeightPx]
  );

  const renderSelectionOverlay = React.useCallback(
    (
      block: CanvasSectionBlock,
      overlayStyle?: React.CSSProperties,
      overlayRef?: React.Ref<HTMLDivElement>,
      options?: { topChrome?: boolean; canvasLayer?: boolean }
    ) => {
      const isActiveImageAdjustBlock =
        block.type === 'image' && isImageAdjustMode && activeAdjustBlockId === block.id;
      if (isActiveImageAdjustBlock) return null;
      if (block.type === 'text' && editingTextBlockId === block.id) return null;
      const isSelected = selectedBlockId === block.id || selectedBlockIds.has(block.id);
      if (!options?.topChrome && showSelectionChrome && isSelected) return null;
      const builderLayerLocked = isAdmin && isBuilderLayerLocked(block);
      const builderLayerHidden = isAdmin && isBuilderLayerHidden(block);
      const canManipulateSelection = !builderLayerLocked && !builderLayerHidden;
      const isPrimarySelection = selectedBlockId === block.id;
      const multiSelectActive = selectedBlockIds.size > 1;
      const cachedEditorBounds = editorBoundsByBlockId[block.id];
      const liveEditorBounds =
        isAdmin && editorBoundsNeedsContentRefresh(cachedEditorBounds)
          ? resolveEditorBoundsForLayer(block) ?? cachedEditorBounds
          : cachedEditorBounds ?? (isAdmin ? resolveEditorBoundsForLayer(block) : undefined);
      const editorOverlayStyle = isAdmin
        ? options?.canvasLayer
          ? editorBoundsCanvasOverlayStyle(liveEditorBounds)
          : editorBoundsFrameOverlayStyle(liveEditorBounds)
        : undefined;
      const isSectionBackdropViewer = isSectionBackdropModelViewerBlock(block);
      const resolvedOverlayStyle =
        overlayStyle ?? editorOverlayStyle ?? (options?.canvasLayer ? resolveCanvasFrameOverlayStyle(block) : undefined);
      const usesEditorBoundsOverlay = !overlayStyle && Boolean(editorOverlayStyle);
      return (
        <SelectionOverlay
          block={block}
          label={isPrimarySelection ? resolveSelectionOverlayLabel(block) : undefined}
          enabled={showSelectionChrome}
          isSelected={isSelected}
          isPrimarySelection={isPrimarySelection}
          multiSelectActive={multiSelectActive}
          showControls={
            canManipulateSelection &&
            !isSectionBackdropViewer &&
            ((moveMode && isSelected) || (scaleMode && block.type === 'product-card' && isPrimarySelection))
          }
          showResizeControls={isPrimarySelection}
          muted={isInteractPreview || builderLayerLocked || builderLayerHidden}
          scaleMode={scaleMode && block.type === 'product-card'}
          overlayStyle={resolvedOverlayStyle}
          overlayInsetPx={usesEditorBoundsOverlay ? 2 : undefined}
          overlayRef={overlayRef}
          onBeginTransform={beginTransform}
          onMoveTransform={moveTransform}
          onEndTransform={endTransform}
          onBeginScale={beginScale}
          onScaleMove={moveScale}
          onScaleEnd={endScale}
          onStartTextEdit={adminPreview?.onStartTextEdit}
        />
      );
    },
    [
      adminPreview?.onStartTextEdit,
      activeAdjustBlockId,
      beginTransform,
      beginScale,
      endTransform,
      endScale,
      editingTextBlockId,
      editorBoundsByBlockId,
      isImageAdjustMode,
      isAdmin,
      isInteractPreview,
      moveMode,
      moveTransform,
      moveScale,
      resolveCanvasFrameOverlayStyle,
      resolveEditorBoundsForLayer,
      scaleMode,
      selectedBlockId,
      selectedBlockIds,
      showSelectionChrome,
    ]
  );

  const renderLogicNodeIdTag = React.useCallback(
    (_block: CanvasSectionBlock) => null,
    []
  );

  const resolveActiveVisualOverlayStyle = React.useCallback(
    (block: CanvasSectionBlock): React.CSSProperties | undefined => {
      const cachedEditorBounds = editorBoundsByBlockId[block.id];
      const liveEditorBounds =
        isAdmin
          ? resolveEditorBoundsForLayer(block) ?? cachedEditorBounds
          : cachedEditorBounds;
      const editorOverlayStyle = isAdmin
        ? editorBoundsCanvasOverlayStyle(liveEditorBounds)
        : undefined;
      return editorOverlayStyle ?? resolveCanvasFrameOverlayStyle(block);
    },
    [editorBoundsByBlockId, isAdmin, resolveCanvasFrameOverlayStyle, resolveEditorBoundsForLayer]
  );

  const renderHoverVisualOutline = React.useCallback(
    (block: CanvasSectionBlock) => {
      if (shouldSuppressBuilderHoverChrome(block)) return null;
      if (block.type === 'text' && editingTextBlockId === block.id) return null;
      const overlayStyle = resolveActiveVisualOverlayStyle(block);
      if (!overlayStyle) return null;
      return (
        <div
          className="pointer-events-none absolute rounded-xl border border-blue-300/55 shadow-[0_0_0_1px_rgba(59,130,246,0.12)]"
          style={overlayStyle}
          data-builder-hover-visual="true"
        />
      );
    },
    [editingTextBlockId, resolveActiveVisualOverlayStyle]
  );

  return (
    <SectionElementLayerCanvas
      sectionId={sectionId}
      settings={settings}
      designSystemOverride={designSystemOverride}
      enquiryEmailTo={enquiryEmailTo}
      onSelectVase={onSelectVase}
      onEnquirySubmit={onEnquirySubmit}
      onNavigateToPage={onNavigateToPage}
      className={className}
      adminPreview={adminPreview}
      flow={flow}
      isAdmin={isAdmin}
      isInteractPreview={isInteractPreview}
      isImmersivePreview={isImmersivePreview}
      isImageAdjustMode={isImageAdjustMode}
      activeAdjustBlockId={activeAdjustBlockId}
      moveMode={moveMode}
      scaleMode={scaleMode}
      showSelectionChrome={showSelectionChrome}
      layerRef={layerRef}
      renderBlocks={renderBlocks}
      selectedBlockId={selectedBlockId}
      selectedBlockIds={selectedBlockIds}
      activeProjectionRows={activeProjectionRows}
      alignmentGuides={alignmentGuides}
      isTransforming={isTransforming}
      isPlacementDrag={isPlacementDrag}
      editorBoundsByBlockId={editorBoundsByBlockId}
      beginTransform={beginTransform}
      moveTransform={moveTransform}
      endTransform={endTransform}
      beginScale={beginScale}
      moveScale={moveScale}
      endScale={endScale}
      activeModelPreview={activeModelPreview}
      setActiveModelPreview={setActiveModelPreview}
      activeImageInteraction={activeImageInteraction}
      beginSurfaceMove={beginSurfaceMove}
      moveSurfaceMove={moveSurfaceMove}
      endSurfaceMove={endSurfaceMove}
      handleWheelZoom={handleWheelZoom}
      beginHandleInteraction={beginHandleInteraction}
      moveHandleInteraction={moveHandleInteraction}
      endHandleInteraction={endHandleInteraction}
      editingTextBlockId={editingTextBlockId}
      setTextMeasureNode={setTextMeasureNode}
      richTextFontOptions={richTextFontOptions}
      typography={typography}
      fontLibrary={fontLibrary}
      useFluidSectionTypography={useFluidSectionTypography}
      usesFixedFrameText={usesFixedFrameText}
      productRecords={productRecords}
      selectedProductIds={selectedProductIds}
      layerSize={layerSize}
      columnWidthPx={columnWidthPx}
      rowHeightPx={rowHeightPx}
      hoveredProductCardId={hoveredProductCardId}
      setHoveredProductCardId={setHoveredProductCardId}
      productCardRuntimeSnapshotsRef={productCardRuntimeSnapshotsRef}
      productCardWrapperNodesRef={productCardWrapperNodesRef}
      productCardArticleNodesRef={productCardArticleNodesRef}
      productCardOverlayNodesRef={productCardOverlayNodesRef}
      setProductCardMeasureNode={setProductCardMeasureNode}
      visibleColourCombinations={visibleColourCombinations}
      viewerRandomSelections={viewerRandomSelections}
      renderLogicNodeIdTag={renderLogicNodeIdTag}
      renderSelectionOverlay={renderSelectionOverlay}
      renderHoverVisualOutline={renderHoverVisualOutline}
      hoveredBlockId={hoveredBlockId}
      setHoveredBlockId={setHoveredBlockId}
    />
  );
};

export default SectionElementLayer;



